import type { User, GeolocationComponents, AddressComponent, SurveyResponse, GeolocationCoordinates, UserType } from '@/types'
import { defineStore, storeToRefs } from 'pinia'
import { Ref, ref, computed } from 'vue'
import { useLazyQuery } from '@vue/apollo-composable'
import { useAmplitude } from '@outputinc/o-analytics'
import { parseToken } from '@outputinc/o-logins'
import { fetchGeolocation as fetchGeolocationQuery, fetchSubscriptionsAccount as fetchSubscriptionsAccountQuery } from '@/queries'
import { useAuthStore } from '@/store/AuthStore'
import { useCheckoutStore } from '@/store/CheckoutStore'
import { useSubscriptionStore } from '@/store/SubscriptionStore'
import { useFeatureFlagStore } from '@/store/FeatureFlagStore'

import { sentryCapture } from '@/utils/sentry'
import reduceUserBillingInfo from '@/composables/reduceUserBillingInfo'

export const useUserStore = defineStore('UserStore', () => {
  const authStore = useAuthStore()
  const subscriptionStore = useSubscriptionStore()
  const { setUserContext } = useFeatureFlagStore()
  const { setUserProperties, UserProperties } = useAmplitude()

  const baseState: User = {
    email: '',
    outputUid: 0,
    location: null,
    customerSurveyResponses: [],
    billingInfo: null,
    firstName: '',
    lastName: '',
    type: null
  }

  const user: Ref<User> = ref(baseState)

  const { hasAnyExpiredSubscription, hasExpiredSubscriptionForCurrentProduct } = storeToRefs(subscriptionStore)

  /* initialize a (recognized) user with JWT and query data */
  const initialize = (type?: UserType): void => {
    const { token } = storeToRefs(authStore)
    const { setCheckoutProperty } = useCheckoutStore()

    if (outputUid.value) {
      return
    }

    try {
      const { email, outputUid } = parseToken(token.value)

      user.value = {
        ...user.value,
        email,
        outputUid
      }
      // set the LD user context
      setUserContext(outputUid)
    } catch (err) {
      sentryCapture(err)
    }

    if (type) {
      setUserType(type)
    }

    // set the email and accountCode in checkout store
    setCheckoutProperty({ email, accountCode: outputUid })
  }

  const setUserLocation = (location: any): void => {
    const { setCheckoutCurrency, setCheckoutProperty } = useCheckoutStore()
    if (!location?.countryCode) {
      return
    }

    const { countryCode: country } = location

    user.value = {
      ...user.value,
      location
    }

    setCheckoutProperty({ country })

    setCheckoutCurrency(country)
  }

  const {
    onResult: onGeolocationResult,
    load: loadUserLocation,
    onError: onGeolocationError
  } = useLazyQuery(
    fetchGeolocationQuery,
    { type: 'country|postal_code' },
    {
      fetchPolicy: 'no-cache'
    }
  )

  onGeolocationError(err => sentryCapture(err))

  onGeolocationResult(response => {
    const {
      data: {
        fetchGeocode: { results }
      }
    } = response
    const closestResult = results.find((components: GeolocationComponents) => components.addressComponents.length > 0)
    const defaultLocation = {
      ...(closestResult && { ...closestResult.geometry?.location })
    }

    const location = closestResult?.addressComponents?.reduce((locationAcc: GeolocationCoordinates, component: AddressComponent) => {
      return {
        ...locationAcc,
        ...(component.types.includes('country') && { countryCode: component.shortName })
      }
    }, defaultLocation)

    if (location) {
      setUserLocation(location)
    }
  })

  const fetchUserLocation = (): void => {
    const isLocationSet = user.value.location !== null
    if (isLocationSet) {
      return
    }

    loadUserLocation()
  }

  const customerSurveyResponses = computed(() => user.value.customerSurveyResponses)

  const surveysHasAnswered = computed(() =>
    user.value.customerSurveyResponses.map(response => {
      const [responseKey] = Object.keys(response)

      return responseKey
    })
  )

  const setCustomerSurveyResponses = (response: SurveyResponse[]) => {
    user.value.customerSurveyResponses = [...user.value.customerSurveyResponses, ...response]
  }

  const {
    onResult: onSubscriptionsAccountResponse,
    load: fetchSubscriptionsAccount,
    onError: onSubscriptionsAccountError
  } = useLazyQuery(fetchSubscriptionsAccountQuery, {
    fetchPolicy: 'cache-first'
  })

  onSubscriptionsAccountError(err => sentryCapture(err))

  onSubscriptionsAccountResponse(response => {
    const { setCheckoutProperty, setCheckoutCurrency } = useCheckoutStore()
    if (!response.data) {
      return
    }

    const {
      data: { fetchSubscriptionsAccount: subscriptionsAccountResult }
    } = response
    const { billingInfo: billingInfoResponse, firstName, lastName } = subscriptionsAccountResult

    const hasBillingInfo = !!billingInfoResponse
    setUserProperties({ [UserProperties.HAS_EXISTING_PAYMENT_METHOD]: hasBillingInfo })

    if (!billingInfoResponse) {
      return
    }

    const billingInfo = reduceUserBillingInfo(billingInfoResponse)
    const { address } = billingInfo

    setCheckoutProperty({
      paymentDetails: billingInfo,
      firstName,
      lastName,
      billingAddress: address
    })

    setCheckoutCurrency(address.country)

    user.value = {
      ...user.value,
      billingInfo,
      firstName,
      lastName
    }
  })

  const resetUser = (): void => {
    user.value = {
      ...baseState
    }
  }

  const setUserType = (type: UserType) => {
    user.value = {
      ...user.value,
      type
    }
  }

  const outputUid = computed(() => user.value.outputUid)
  const email = computed(() => user.value.email)
  const location = computed(() => user.value.location)
  const country = computed(() => location.value?.countryCode)
  const isUSUser = computed(() => location.value?.countryCode === 'US')
  const billingInfo = computed(() => user.value.billingInfo)
  const userType = computed(() => user.value.type)
  const isAnyOutputResubscriber = computed(() => hasAnyExpiredSubscription.value)
  const isDirectResubscriber = computed(() => hasExpiredSubscriptionForCurrentProduct.value)

  return {
    billingInfo,
    country,
    customerSurveyResponses,
    email,
    fetchSubscriptionsAccount,
    fetchUserLocation,
    initialize,
    isAnyOutputResubscriber,
    isDirectResubscriber,
    isUSUser,
    onSubscriptionsAccountResponse,
    outputUid,
    resetUser,
    setCustomerSurveyResponses,
    setUserLocation,
    setUserType,
    surveysHasAnswered,
    userType
  }
})
